/* global impersonateUserUrl, organisationsUrl */

import React, { useState, useEffect, Fragment, useRef } from 'react';

import { Modal } from 'flowbite';

import AccountsDelete from './AccountsDelete';
import AccountsTableRow from './AccountsTableRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TextButton from 'CommonComponents/TextButton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import TrashIcon from 'Assets/icons/trash.svg';

function AccountsTable(props) {
    const [accounts, setAccounts] = useState([]);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);
    const [deletedOrganisation, setDeletedOrganisation] = useState(null);
    const [deletedAccount, setDeletedAccount] = useState(null);
    const [updatedAccount, setUpdatedAccount] = useState(null);

    const [impersonatedUser, setImpersonatedUser] = useState(null);
    const [impersonatedEvent, setImpersonatedEvent] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [accountsDeleteModalElement, setAccountsDeleteModalElement] = useState(null);
    const [deletedOrganisationModalElement, setDeletedOrganisationModalElement] = useState(null);
    const [updatedAccountModalElement, setUpdatedAccountModalElement] = useState(null);

    const [userImpersonateConfirmModalElement, setUserImpersonateConfirmModalElement] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const accountsDeleteModalRef = useRef();
    const deletedOrganisationModalRef = useRef();
    const userImpersonateConfirmModalRef = useRef();

    useEffect(() => {
        fetchAccounts();
        accountsDeleteModalRef && setAccountsDeleteModalElement(new Modal(accountsDeleteModalRef.current, modalOptions(false)));
        deletedOrganisationModalRef && setDeletedOrganisationModalElement(new Modal(deletedOrganisationModalRef.current, modalOptions(false)));
        userImpersonateConfirmModalRef && setUserImpersonateConfirmModalElement(new Modal(userImpersonateConfirmModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (deletedAccount) {
            accountsDeleteModalElement && accountsDeleteModalElement.show();
        }
    }, [deletedAccount]);

    useEffect(() => {
        if (deletedOrganisation) {
            deletedOrganisationModalElement && deletedOrganisationModalElement.show();
        }
    }, [deletedOrganisation]);

    useEffect(() => {
        if (updatedAccount) {
            updatedAccountModalElement && updatedAccountModalElement.show();
        }
    }, [updatedAccount]);

    useEffect(() => {
        if (impersonatedUser) {
            userImpersonateConfirmModalElement && userImpersonateConfirmModalElement.show();
        }
    }, [impersonatedUser]);

    const defaultUrl = props.fetchAccountsUrl + '?per_page=' + perPage;

    const combineFetchUrl = (p = null, pp = null) => {
        return props.fetchAccountsUrl + '?per_page=' + (pp || perPage) + '&page=' + (p || page);
    };

    const fetchAccounts = (url = null) => {
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data } = res.data;
                setAccounts(data);
                setTo(res.data.meta.to);
                setFrom(res.data.meta.from);
                setTotal(res.data.meta.total);
                setLastPage(res.data.meta.last_page);

                const expandedData = JSON.parse(JSON.stringify(expanded));
                data.forEach((org) => {
                    expandedData[org.id] = expandedData[org.id] !== undefined ? expandedData[org.id] : false;
                });
                setExpanded(expandedData);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const changeExpanded = (id) => {
        const expandedData = JSON.parse(JSON.stringify(expanded));
        expandedData[id] = !expandedData[id];
        setExpanded(expandedData);
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchAccounts(url);
    }

    const confirmDeletedAccount = (account) => {
        setDeletedAccount(null);
        setDeletedAccount(account);
    };

    const confirmDeletedOrganisation = (organisation) => {
        setDeletedOrganisation(null);
        setDeletedOrganisation(organisation);
    };

    const editAccount = (account) => {
        setUpdatedAccount(null);
        setUpdatedAccount(account);
    };

    const editOrganisationModules = (organisation) => {
        setUpdatedAccount(null);
        setUpdatedAccount(organisation);
    };

    const closeAccountModal = () => {
        accountsDeleteModalElement && accountsDeleteModalElement.hide();
        deletedOrganisationModalElement && deletedOrganisationModalElement.hide();
        updatedAccountModalElement && updatedAccountModalElement.hide();
        setUpdatedAccount(null);
        setDeletedOrganisation(null);
        setDeletedAccount(null);
    };

    const closeConfirmImpersonate = () => {
        userImpersonateConfirmModalElement && userImpersonateConfirmModalElement.hide();
        setImpersonatedUser(null);
    };

    const successHandler = () => {
        setAccounts([]);
        const url = combineFetchUrl(page);
        fetchAccounts(url);
        accountsDeleteModalElement && accountsDeleteModalElement.hide();
        deletedOrganisationModalElement && deletedOrganisationModalElement.hide();
        updatedAccountModalElement && updatedAccountModalElement.hide();
        setUpdatedAccount(null);
        setDeletedOrganisation(null);
        setDeletedAccount(null);
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteOrganisation = () => {
        setActionInProgress(true);

        const slug = deletedOrganisation.slug;
        const url = props.deleteOrganisationUrl.replace('ORGANISATION_SLUG', slug);

        axios
            .delete(url)
            .then((response) => {
                setActionCompleted(true);
            })
            .catch((e) => {
                setActionFailed(true);
            })
            .finally(() => {
                setActionInProgress(false);
            });
    };

    const triggerImpersonate = () => {
        setActionInProgress(true);
        const url = impersonateUserUrl.replace('USER_ID', impersonatedUser.id);

        axios
            .get(url)
            .then(() => {
                setActionCompleted(true);
                window.location.href = props.eventHomeUrl.replace('EVENT_SLUG', impersonatedEvent.slug);
            })
            .catch((e) => {
                console.error(e);
            });
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
                <div>
                    <h4 className="table__header">Accounts</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={to} from={from} total={total} />
                        <TablePagination lastPage={lastPage} pageChange={pageChange} />
                    </div>
                </div>
                <Button variant="primary" as="a" href={organisationsUrl}>
                    {$t('Organisations')}
                </Button>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-table-fixed">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th className="tw-min-w-40">Key Contact</th>
                            <th className="tw-w-20">Seats</th>
                            <th className="tw-w-20">Users</th>
                            {!!props.featureEventDeletion && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.map((account) => {
                            const show = expanded[account.id] || false;

                            return (
                                <Fragment key={account.id}>
                                    <tr>
                                        <td
                                            colSpan={props.featureEventDeletion ? 4 : 3}
                                            className={`${account.events.length > 0 ? 'tw-cursor-pointer' : ''} `}
                                            onClick={(e) => changeExpanded(account.id)}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-x-2">
                                                <div className="tw-font-semibold">{account.name}</div>
                                                {account.events.length > 0 &&
                                                    (show ? (
                                                        <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" />
                                                    ) : (
                                                        <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" />
                                                    ))}
                                            </div>
                                        </td>
                                        <td>
                                            <TextButton variant="danger" onClick={() => confirmDeletedOrganisation(account)}>
                                                <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete Organisation')}
                                            </TextButton>
                                        </td>
                                    </tr>
                                    {show && (
                                        <tr className="tw-align-top !tw-bg-white">
                                            <td colSpan={props.featureEventDeletion ? 5 : 6}>
                                                <div className="tw-text-center tw-mb-2">
                                                    {$t('Modules of the Subscription Plan [{{name}}]', { name: account.subscription_plan.name })}
                                                </div>
                                                <div className={'tw-flex tw-justify-center'}>
                                                    {account.subscription_plan.modulesWithSubModules.map((module) => {
                                                        return (
                                                            <div key={`module_${module.id}`} className="tw-flex tw-flex-col tw-mr-4">
                                                                <span className="tw-flex tw-items-center tw-mr-3">
                                                                    <CircleCheckIcon className="tw-w-3 tw-h-3 tw-text-green" aria-hidden="true" />
                                                                    <span className="tw-ml-2">{` ${module.name}`}</span>
                                                                </span>
                                                                {module.sub_modules.length > 0 && (
                                                                    <ul>
                                                                        {module.sub_modules.map((subModule) => {
                                                                            return (
                                                                                <li key={subModule.id}>
                                                                                    <span className="tw-flex tw-items-center tw-ml-2">
                                                                                        <CircleCheckIcon
                                                                                            className="tw-w-3 tw-h-3 tw-text-green"
                                                                                            aria-hidden="true"
                                                                                        />
                                                                                        <span className="tw-ml-2">{` ${subModule.name}`}</span>
                                                                                    </span>
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {account.events.map((event) => {
                                        return (
                                            <AccountsTableRow
                                                fetchUsersUrl={props.fetchUsersUrl}
                                                featureEventDeletion={props.featureEventDeletion}
                                                key={event.id}
                                                account={event}
                                                confirmDeleteHandler={confirmDeletedAccount}
                                                editAccount={editAccount}
                                                show={show}
                                                onImpersonate={(user) => {
                                                    setImpersonatedEvent(event);
                                                    setImpersonatedUser(user);
                                                }}
                                            />
                                        );
                                    })}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <AccountsDelete
                modalId={'delete-account'}
                modalRef={accountsDeleteModalRef}
                modalElement={accountsDeleteModalElement}
                closeHandler={closeAccountModal}
                account={deletedAccount}
                deleteEventsUrl={props.deleteEventsUrl}
                successDeletedHandler={successHandler}
            />
            <ConfirmationModal
                successHandler={successHandler}
                closeHandler={closeAccountModal}
                triggerAction={triggerDeleteOrganisation}
                cleanStates={cleanConfirmationModalStates}
                allowAction={deletedOrganisation != undefined}
                modalId={`delete-organisation-${deletedOrganisation?.id}`}
                modalRef={deletedOrganisationModalRef}
                modalElement={deletedOrganisationModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t(
                    'Are you sure you want to delete the organisation "{{name}}"? All events belonging to this organisation will also be deleted.',
                    { name: deletedOrganisation?.name },
                )}
                actionCompletedMsg={$t('[{{name}}] deleted', { name: deletedOrganisation?.name })}
                actionFailedMsg={$t('The organisation could not be deleted')}
                title={$t('Delete Organisation')}
                cancelButtonText={$t('Close')}
                primaryButtonText={$t('Delete')}
            />
            <ConfirmationModal
                successHandler={() => {
                    window.location.href = props.eventHomeUrl.replace('EVENT_SLUG', impersonatedEvent?.slug);
                }}
                closeHandler={closeConfirmImpersonate}
                triggerAction={triggerImpersonate}
                cleanStates={cleanConfirmationModalStates}
                allowAction={impersonatedUser != undefined}
                modalId="impersonate-user-confirm"
                modalRef={userImpersonateConfirmModalRef}
                modalElement={userImpersonateConfirmModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Impersonate {{name}} now?', {
                    name: `${impersonatedUser?.first_name} ${impersonatedUser?.last_name}`,
                })}
                actionCompletedMsg=""
                actionFailedMsg=""
                title={$t('impersonate')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Yes')}
            />
        </div>
    );
}

export default AccountsTable;
